/** Bascule clair/sombre, et lecture des couleurs du thème par les graphiques. */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";

type Mode = "light" | "dark";

const KEY = "capalys.theme";

const ThemeContext = createContext<{ mode: Mode; toggle: () => void }>({
  mode: "light",
  toggle: () => {},
});

function initial(): Mode {
  const stored = localStorage.getItem(KEY);
  if (stored === "light" || stored === "dark") return stored;
  // Sans préférence enregistrée, on suit le système : un atelier réglé en sombre
  // n'a pas à recevoir une page blanche au premier chargement.
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<Mode>(() => {
    try {
      return initial();
    } catch {
      return "light";
    }
  });

  useEffect(() => {
    document.documentElement.classList.toggle("dark", mode === "dark");
    try {
      localStorage.setItem(KEY, mode);
    } catch {
      // Navigation privée : le thème ne survit pas à la session, sans conséquence.
    }
  }, [mode]);

  const toggle = useCallback(
    () => setMode((m) => (m === "dark" ? "light" : "dark")),
    [],
  );
  const value = useMemo(() => ({ mode, toggle }), [mode, toggle]);

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

/**
 * Couleurs du thème courant, lues depuis le CSS.
 *
 * ECharts dessine sur un canvas : il ne connaît ni les variables CSS ni les classes
 * Tailwind, il lui faut des couleurs littérales. Les relire ici plutôt que de les
 * redéclarer évite la dérive — le jour où la palette change, les graphiques suivent.
 */
export function useChartPalette() {
  const { mode } = useTheme();

  return useMemo(() => {
    const read = (name: string, fallback: string) => {
      if (typeof window === "undefined") return fallback;
      const raw = getComputedStyle(document.documentElement)
        .getPropertyValue(name)
        .trim();
      return raw ? `rgb(${raw})` : fallback;
    };
    return {
      mode,
      ink: read("--c-text", "#18181b"),
      muted: read("--c-text-muted", "#6a6a70"),
      grid: read("--c-grid", "#e8e8e6"),
      surface: read("--c-surface", "#ffffff"),
      accent: read("--c-accent", "#1b3a5c"),
      good: read("--c-good", "#15803d"),
      warn: read("--c-warn", "#b45309"),
      bad: read("--c-bad", "#b91c1c"),
    };
  }, [mode]);
}
